<template>
  <div class="bg-white/95 backdrop-blur-sm py-8 px-4 shadow-lg rounded-lg sm:px-10 w-full max-w-md mx-auto relative">
    <button @click="$emit('close')" class="absolute top-4 right-4 text-gray-400 hover:text-gray-500">
      <span class="sr-only">{{ $t('completeProfileModal.close') }}</span>
      <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>

    <h3 class="text-center text-xl font-bold text-gray-900 mb-6">
      {{ $t('completeProfileModal.title') }}
    </h3>

    <form class="space-y-6" @submit.prevent="updateProfile">
      <div>
        <label for="dni" class="block text-sm font-medium text-gray-700">{{ $t('completeProfile.dni.label') }}</label>
        <input v-model="dni" type="text" id="dni" required @input="handleDniInput"
          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
          :class="{ 'border-red-500': dniError }" :placeholder="$t('completeProfile.dni.placeholder')">
        <p v-if="dniError" class="mt-1 text-sm text-red-600">{{ dniError }}</p>
        <p v-if="dniValidationMessage" class="mt-1 text-sm text-green-600">{{ dniValidationMessage }}</p>
        <div class="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded-md flex items-start">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-indigo-500 mt-0.5 mr-1.5 flex-shrink-0"
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{{ $t('completeProfileModal.idPhotos.securityInfo') }}</span>
        </div>
      </div>

      <!-- Solo para propietarios -->
      <div v-if="userType === 'owner'">
        <label for="bankNumber" class="block text-sm font-medium text-gray-700">{{
          $t('completeProfile.bankAccount.label') }}</label>
        <input v-model="bankNumber" type="text" id="bankNumber"
          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
          :class="{ 'border-red-500': bankNumberError }"
          placeholder="ES12 3456 7890 1234 5678 90" @input="formatBankNumber">
        <p v-if="bankNumberError" class="mt-1 text-sm text-red-600">{{ bankNumberError }}</p>
        <p v-else class="mt-1 text-sm text-gray-500">{{ $t('completeProfile.bankAccount.formatInfo') }}</p>
        
       
        
        <div class="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded-md flex items-start">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-indigo-500 mt-0.5 mr-1.5 flex-shrink-0"
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{{ $t('completeProfile.bankAccount.info') }}</span>
        </div>
      </div>

      <!-- Campos para inquilinos -->

      <div v-if="userType === 'applicant'">
        <label for="address" class="block text-sm font-medium text-gray-700">Dirección habitual</label>
        <input v-model="address" type="text" id="address"
          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900">
        <div class="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded-md flex items-start">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-indigo-500 mt-0.5 mr-1.5 flex-shrink-0"
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Necesitamos conocer tu dirección habitual para poder contactarte en caso de que necesitemos más información.</span>
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('completeProfile.dniPhotos.frontLabel')
        }}</label>
        <div class="mt-1 flex items-center">
          <input type="file" ref="dniFrontInput" accept="image/*" class="hidden" @change="handleDniFrontUpload">
          <button type="button" @click="$refs.dniFrontInput.click()"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            <svg class="-ml-1 mr-2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none"
              viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {{ dniFrontPictureUrl ? $t('completeProfile.dniPhotos.frontChange') :
              $t('completeProfile.dniPhotos.frontUpload') }}
          </button>
          <span v-if="dniFrontPictureUrl" class="ml-3 text-sm text-green-600">
            {{ $t('completeProfile.dniPhotos.frontUploaded') }}
          </span>
        </div>
        
        <!-- Estado de verificación OCR para frontal -->
        <div v-if="dniFrontPictureUrl" class="mt-3">
          <div class="flex items-center space-x-2">
            <!-- Verificando -->
            <div v-if="ocrVerificationStatus.front.status === 'verifying'" class="flex items-center text-blue-600">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="text-sm">Verificando documento...</span>
            </div>
            
            <!-- Verificado -->
            <div v-else-if="ocrVerificationStatus.front.status === 'verified'" class="flex items-center text-green-600">
              <svg class="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span class="text-sm">DNI verificado correctamente</span>
              <span v-if="ocrVerificationStatus.front.confidence" class="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                {{ Math.round(ocrVerificationStatus.front.confidence * 100) }}% confianza
              </span>
            </div>
            
            <!-- Error -->
            <div v-else-if="ocrVerificationStatus.front.status === 'failed'" class="flex items-center text-red-600">
              <svg class="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span class="text-sm">{{ ocrVerificationStatus.front.message }}</span>
              <button @click="retryOcrVerification('front')" 
                      class="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded hover:bg-red-200 transition-colors">
                Reintentar
              </button>
            </div>
            
            <!-- Pendiente -->
            <div v-else-if="ocrVerificationStatus.front.status === 'pending' && dni" class="flex items-center text-gray-500">
              <svg class="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span class="text-sm">Pendiente de verificación</span>
              <button @click="retryOcrVerification('front')" 
                      class="ml-2 text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded hover:bg-gray-200 transition-colors">
                Verificar ahora
              </button>
            </div>
          </div>
        </div>
        
        <img v-if="dniFrontPictureUrl" :src="dniFrontPictureUrl" class="mt-2 h-32 w-auto rounded-lg object-cover"
          alt="DNI frente">

      </div>

      

      <!-- Texto explicativo general sobre seguridad y protección -->
      <div class="space-y-4 mt-6">
        <!-- Seguridad de datos generales -->
        <div class="bg-blue-50 border border-blue-200 rounded-md">
          <button type="button" @click="toggleDataSecurity"
            class="w-full flex items-center justify-between p-4 text-left focus:outline-none">
            <div class="flex items-center">
              <svg class="h-5 w-5 text-blue-600 mr-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                fill="currentColor">
                <path fill-rule="evenodd"
                  d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd" />
              </svg>
              <h3 class="text-sm font-medium text-blue-800">{{ $t('completeProfile.info.dataSecurity.title') }}</h3>
            </div>
            <svg class="h-5 w-5 text-blue-600 transform transition-transform duration-200"
              :class="{ 'rotate-180': showDataSecurity }" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
              fill="currentColor">
              <path fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd" />
            </svg>
          </button>
          <div v-show="showDataSecurity" class="px-4 pb-4 text-sm text-blue-700">
            <p class="mb-2">{{ $t('completeProfile.info.dataSecurity.description') }}</p>
            <ul class="list-disc list-inside space-y-1 ml-2">
              <li>Cifrado de datos con estándares AES-256 y SSL/TLS</li>
              <li>Almacenamiento en servidores seguros con certificación ISO 27001</li>
              <li>Acceso restringido solo al personal autorizado</li>
              <li>Verificación de identidad mediante tecnología OCR avanzada</li>
              <li>Tokenización de datos bancarios para transacciones seguras</li>
              <li>Cumplimiento estricto del RGPD y la LOPD</li>
            </ul>
            <p class="mt-2">{{ $t('completeProfile.info.dataSecurity.footer') }}</p>
          </div>
        </div>

        <!-- Verificación de Identidad -->
        <div class="bg-blue-50 border-l-4 border-blue-400">
          <button type="button" @click="toggleVerification"
            class="w-full flex items-center justify-between p-4 text-left focus:outline-none">
            <div class="flex items-center">
              <svg class="h-5 w-5 text-blue-400 mr-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                fill="currentColor">
                <path fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clip-rule="evenodd" />
              </svg>
              <h3 class="text-sm font-medium text-blue-800">{{ $t('completeProfile.info.verification.title') }}</h3>
            </div>
            <svg class="h-5 w-5 text-blue-400 transform transition-transform duration-200"
              :class="{ 'rotate-180': showVerification }" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
              fill="currentColor">
              <path fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd" />
            </svg>
          </button>
          <div v-show="showVerification" class="px-4 pb-4 text-sm text-blue-700">
            <p>{{ $t('completeProfile.info.verification.description') }}</p>
            <ul class="list-disc list-inside mt-2 space-y-1">

              <li>Verificar la identidad real de los usuarios</li>
              <li>Prevenir fraudes y estafas</li>
              <li>Crear un entorno seguro para la gestión de contratos</li>
            </ul>
          </div>
        </div>

        <!-- Protección de Datos -->
        <div class="bg-green-50 border-l-4 border-green-400">
          <button type="button" @click="togglePrivacy"
            class="w-full flex items-center justify-between p-4 text-left focus:outline-none">
            <div class="flex items-center">
              <svg class="h-5 w-5 text-green-400 mr-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                fill="currentColor">
                <path fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd" />
              </svg>
              <h3 class="text-sm font-medium text-green-800">{{ $t('completeProfile.info.privacy.title') }}</h3>
            </div>
            <svg class="h-5 w-5 text-green-400 transform transition-transform duration-200"
              :class="{ 'rotate-180': showPrivacy }" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
              fill="currentColor">
              <path fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd" />
            </svg>
          </button>
          <div v-show="showPrivacy" class="px-4 pb-4 text-sm text-green-700">
            <p>{{ $t('completeProfile.info.privacy.description') }}</p>
            <ul class="list-disc list-inside mt-2 space-y-1">
              <li>Solo serán visibles cuando se asigne un interesado al contrato</li>
              <li>Están protegidos con las más altas medidas de seguridad</li>
              <li>No serán compartidos con terceros sin tu consentimiento</li>
            </ul>
          </div>
        </div>
      </div>

      <div>
        <button type="submit" :disabled="isLoading || !isFormValid"
          class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
          {{ isLoading ? $t('completeProfile.buttons.submitting') : $t('completeProfile.buttons.submit') }}
        </button>
      </div>
    </form>

    <!-- Toast de mensaje -->
    <div v-if="toastMessage"
      class="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, storage } from '../firebase';
import { validateDNIorNIE } from '../utils/dniValidator';
import { validateSpanishIBAN, formatIBANInput } from '../utils/ibanValidator';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Props y emits
const props = defineProps({
  pendingContractData: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['close', 'profile-complete']);

// Estado del formulario
const isLoading = ref(false);
const toastMessage = ref('');
const userType = ref('');
const dni = ref('');
const bankNumber = ref('');
const bankNumberError = ref('');
const address = ref('');
const applicantDescription = ref('');
const monthlyIncome = ref(null);
const dniFrontPictureUrl = ref('');
const dniFrontPicture = ref(null);
const dniError = ref('');
const dniValidationMessage = ref('');

// Estado para mostrar/ocultar secciones de información
const showDataSecurity = ref(false);
const showVerification = ref(false);
const showPrivacy = ref(false);

// Estados de verificación OCR
const ocrVerificationStatus = ref({
  front: { status: 'pending', message: '', confidence: 0 }
});
const isVerifyingOcr = ref(false);

// Función para verificar DNI con OCR
const verifyDniWithOcr = async (imageFile, documentType) => {
  if (!dni.value || !validateDNIorNIE(dni.value)) {
    showToast('Por favor, introduce un DNI válido antes de subir las imágenes');
    return false;
  }

  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    showToast('Error de autenticación');
    return false;
  }

  try {
    isVerifyingOcr.value = true;
    ocrVerificationStatus.value[documentType].status = 'verifying';
    ocrVerificationStatus.value[documentType].message = 'Verificando documento...';

    // Convertir imagen a base64
    const base64Image = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(imageFile);
    });

    // Llamar a la Cloud Function
    const functions = getFunctions();
    const verifyDniDocument = httpsCallable(functions, 'verifyDniDocument');
    const result = await verifyDniDocument({
      imageBase64: base64Image,
      manualDni: dni.value,
      documentType: documentType,
      userId: user.uid
    });

    const { success, confidence, matches, message } = result.data;

    if (success && matches) {
      ocrVerificationStatus.value[documentType].status = 'verified';
      ocrVerificationStatus.value[documentType].message = 'DNI verificado correctamente';
      ocrVerificationStatus.value[documentType].confidence = confidence;
      showToast(`✅ ${documentType === 'front' ? 'Frontal' : 'Dorso'} del DNI verificado correctamente`);
      return true;
    } else {
      ocrVerificationStatus.value[documentType].status = 'failed';
      ocrVerificationStatus.value[documentType].message = message || 'Error en la verificación';
      ocrVerificationStatus.value[documentType].confidence = confidence || 0;
      showToast(`❌ Error: ${message}`);
      return false;
    }
  } catch (error) {
    console.error('Error en verificación OCR:', error);
    ocrVerificationStatus.value[documentType].status = 'failed';
    ocrVerificationStatus.value[documentType].message = 'Error interno de verificación';
    showToast('Error al verificar el documento. Inténtalo de nuevo.');
    return false;
  } finally {
    isVerifyingOcr.value = false;
  }
};

// Función para re-verificar manualmente
const retryOcrVerification = async (documentType) => {
  const file = documentType === 'front' ? dniFrontPicture.value : null;
  if (file) await verifyDniWithOcr(file, documentType);
};

// Métodos para mostrar/ocultar secciones informativas
const toggleDataSecurity = () => {
  showDataSecurity.value = !showDataSecurity.value;
};

const toggleVerification = () => {
  showVerification.value = !showVerification.value;
};

const togglePrivacy = () => {
  showPrivacy.value = !showPrivacy.value;
};

// Validar si el formulario está completo
const isFormValid = computed(() => {
  const frontVerified = ocrVerificationStatus.value.front.status === 'verified';
  const backVerified = ocrVerificationStatus.value.back.status === 'verified';
  
  const baseValid = dni.value &&
    !dniError.value &&
    (dniFrontPictureUrl.value || dniFrontPicture.value) &&
    frontVerified; // Requiere frontal verificado

  if (userType.value === 'owner') {
    return baseValid; // La cuenta bancaria ya no es obligatoria para owners
  } else if (userType.value === 'applicant') {
    return baseValid; // Para applicants solo se requieren DNI y fotos verificadas
  }

  return false; // Si no hay userType definido
});

// Manejar entrada del DNI
const handleDniInput = async (event) => {
  let value = event.target.value.toUpperCase().trim();

  // Eliminar espacios y caracteres no válidos
  value = value.replace(/[^0-9A-Z]/g, '');

  dni.value = value;
  validateDniInput(value);

  // Si el DNI es válido y hay imágenes subidas, re-verificar
  if (validateDNIorNIE(value) && dniFrontPicture.value) {
      await verifyDniWithOcr(dniFrontPicture.value, 'front');
  }
};

// Validar DNI
const validateDniInput = (value) => {
  if (!value) {
    dniError.value = t('completeProfile.dni.required');
    return false;
  }

  // Verificar formato básico
  if (!/^[0-9]{8}[A-Z]$/.test(value) && !/^[XYZ][0-9]{7}[A-Z]$/.test(value)) {
    dniError.value = t('completeProfile.dni.formatError');
    return false;
  }

  // Validar DNI/NIE
  if (!validateDNIorNIE(value)) {
    dniError.value = t('completeProfile.dni.error');
    return false;
  }

  dniError.value = '';
  return true;
};

// Formatear número de cuenta bancaria
const formatBankNumber = (event) => {
  const formattedValue = formatIBANInput(event.target.value);
  bankNumber.value = formattedValue;
  
  // Validar IBAN en tiempo real
  if (formattedValue.length >= 4) {
    const validation = validateSpanishIBAN(formattedValue.replace(/\s/g, ''));
    if (!validation.isValid) {
      bankNumberError.value = validation.error;
    } else {
      bankNumberError.value = '';
    }
  } else {
    bankNumberError.value = '';
  }
};

// Manejar subida de imagen frontal del DNI
const handleDniFrontUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validar tipo de archivo
  if (!file.type.match('image.*')) {
    showToast(t('completeProfile.toasts.imageTypeError'));
    return;
  }

  // Validar tamaño de archivo
  if (file.size > 5 * 1024 * 1024) { // 5MB
    showToast(t('completeProfile.toasts.imageSizeError'));
    return;
  }

  dniFrontPicture.value = file;
  // Mostrar una vista previa de la imagen
  const reader = new FileReader();
  reader.onload = (e) => {
    dniFrontPictureUrl.value = e.target.result;
  };
  reader.readAsDataURL(file);

  // Verificar automáticamente con OCR si hay DNI introducido
  if (dni.value && validateDNIorNIE(dni.value)) {
    await verifyDniWithOcr(file, 'front');
  }
};

// Eliminado: subida de imagen trasera del DNI

// Mostrar mensaje de error/éxito
const showToast = (message) => {
  toastMessage.value = message;
  setTimeout(() => {
    toastMessage.value = '';
  }, 3000);
};

// Función principal para actualizar el perfil
const updateProfile = async () => {
  if (!validateDniInput(dni.value)) {
    return;
  }

  // Validaciones específicas según el tipo de usuario
  // La cuenta bancaria ya no es obligatoria para owners
  // if (userType.value === 'owner' && !bankNumber.value) {
  //   showToast(t('completeProfile.toasts.requiredFields'));
  //   return;
  // }

  // Validar el IBAN para usuarios owner
  if (userType.value === 'owner' && bankNumber.value) {
    const ibanValidation = validateSpanishIBAN(bankNumber.value.replace(/\s/g, ''));
    if (!ibanValidation.isValid) {
      showToast(`Error en IBAN: ${ibanValidation.error}`);
      return;
    }
    // Usar el IBAN limpio para guardar
    bankNumber.value = ibanValidation.iban;
  }

  // Verificar que se han subido las fotos del DNI
  if (!dniFrontPicture.value && !dniFrontPictureUrl.value) {
    showToast(t('completeProfile.toasts.requiredFields'));
    return;
  }

  // Eliminado: requisito de imagen dorso

  // Verificar que al menos una imagen del DNI esté verificada
  const frontVerified = ocrVerificationStatus.value.front.status === 'verified';
  if (!frontVerified) {
    showToast('Por favor, verifica la imagen frontal del DNI antes de continuar');
    return;
  }

  isLoading.value = true;

  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      showToast(t('completeProfile.toasts.authError'));
      isLoading.value = false;
      return;
    }

    // Subir imágenes del DNI
    let frontUrl = dniFrontPictureUrl.value;
    // Eliminado dorso

    // Subir el frontal del DNI si es nuevo
    if (dniFrontPicture.value) {
      const dniFrontPictureRef = storageRef(storage, `dni_pictures/${user.uid}/front`);
      await uploadBytes(dniFrontPictureRef, dniFrontPicture.value);
      frontUrl = await getDownloadURL(dniFrontPictureRef);
    }

    // Subir la parte trasera del DNI si es nueva
    // Eliminado upload dorso

    const userRef = doc(db, 'users', user.uid);

    // Datos base a actualizar
    const updateData = {
      dni: dni.value,
      dniFrontPictureUrl: frontUrl,
      
      hasCompletedProfile: true,
      // Añadir estado de verificación OCR
      dniOcrVerified: frontVerified,
      dniOcrVerificationDate: new Date()
    };

    // Agregar campos específicos según el tipo de usuario
    if (userType.value === 'owner') {
      updateData.bankNumber = bankNumber.value; // Ya está limpio del IBAN
    } else if (userType.value === 'applicant') {
      updateData.applicantDescription = applicantDescription.value;
      updateData.monthlyIncome = monthlyIncome.value;
      updateData.address = address.value;
    }

    await updateDoc(userRef, updateData);

    // Actualizar la caché del perfil en localStorage
    localStorage.setItem('userProfileState', JSON.stringify({
      hasCompletedProfile: true,
      lastChecked: new Date().toISOString()
    }));

    showToast(t('completeProfile.toasts.updateSuccess'));

    // Emitir evento de perfil completado
    emit('profile-complete', { userId: user.uid });
  } catch (error) {
    console.error('Error updating profile:', error);
    showToast(t('completeProfile.toasts.updateError'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  // Detectar si el usuario ya tiene datos guardados
  const fetchUserData = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Cargar userType y datos base
          userType.value = userData.userType || '';
          dni.value = userData.dni || '';
          
          // Cargar datos específicos según el tipo de usuario
          if (userData.userType === 'owner') {
            bankNumber.value = userData.bankNumber || '';
          } else if (userData.userType === 'applicant') {
            applicantDescription.value = userData.applicantDescription || '';
            monthlyIncome.value = userData.monthlyIncome || null;
            address.value = userData.address || '';
          }
          
          // Verificar estado de verificación OCR
          if (userData.dniVerificationStatus === 'verified') {
            ocrVerificationStatus.value.front.status = 'verified';
            ocrVerificationStatus.value.front.message = 'DNI verificado previamente';
            ocrVerificationStatus.value.back.status = 'verified';
            // Eliminado: estado verificado de dorso
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }
  };

  fetchUserData();
});
</script>